import { useSyncExternalStore } from 'react';

export interface QueryErrorEntry {
  id: string;
  timestamp: string;
  queryKey: readonly unknown[];
  message: string;
  stack?: string;
}

// In-memory session counter for the nav badge (real-time, no polling)
let sessionErrorCount = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

/**
 * POST error to the API for persistent storage.
 * Fire-and-forget — never blocks the UI or throws.
 */
function persistToApi(entry: Omit<QueryErrorEntry, 'id' | 'timestamp'>) {
  const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';
  fetch(`${apiUrl}/client-errors`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      queryKey: entry.queryKey,
      message: entry.message,
      stack: entry.stack,
      url: window.location.href,
    }),
  }).catch(() => {
    // Silently ignore — can't report an error about error reporting
  });
}

export function addQueryError(entry: Omit<QueryErrorEntry, 'id' | 'timestamp'>) {
  sessionErrorCount++;
  emit();
  persistToApi(entry);
}

export function clearSessionErrorCount() {
  sessionErrorCount = 0;
  emit();
}

export function getSessionErrorCount(): number {
  return sessionErrorCount;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useSessionErrorCount(): number {
  return useSyncExternalStore(subscribe, getSessionErrorCount, getSessionErrorCount);
}
